import React, { useMemo, useRef, useState } from 'react';

import Tippy from '@tippyjs/react/headless';
import clsx from 'clsx';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import PropTypes from 'prop-types';
dayjs.extend(utc);
dayjs.extend(timezone);

// Hooks
// Don't use user hook because it's used in collaboration space

// Components
import PostEvent from '@/components/composite/Scheduler/Calendar/PostEvent';
import PostEventStackedFilter from '@/components/composite/Scheduler/Calendar/PostEventStackedFilter';

const PostEventInStackWrapper = (props) => {
    const ref = useRef();
    return <PostEvent ref={ref} {...props} />;
};

const PostEventStacked = ({
    post,
    timeFormat,
    timezone,
    stackedPosts,
    onClick = null,
    settings = null,
    clickAction = {},
    isGroup = false,
    spGroups = []
}) => {
    const eventRef = useRef();

    const [profileFilter, setProfileFilter] = useState(null);

    const stackedPostsToDisplay = useMemo(() => {
        let stackedPostsToDisplayTmp = [];
        const isFilterGroup = profileFilter?.social_profiles;
        const stackedPostsSorted = stackedPosts.sort((a, b) => {
            return dayjs(a.scheduled_at).isBefore(dayjs(b.scheduled_at)) ? -1 : 1;
        });

        if (profileFilter === null) {
            return stackedPostsSorted;
        }

        if (isFilterGroup) {
            const group = spGroups.find((group) => group.id === profileFilter.id);
            group?.social_profiles?.forEach((profile) => {
                stackedPostsToDisplayTmp = [
                    ...stackedPostsToDisplayTmp,
                    ...stackedPostsSorted.filter((post) => post.social_profile_id === profile.id)
                ];
            });
        } else {
            stackedPostsToDisplayTmp = stackedPostsSorted.filter(
                (post) => post.social_profile_id === profileFilter.id
            );
        }

        return stackedPostsToDisplayTmp;
    }, [profileFilter, spGroups, stackedPosts]);

    return (
        <Tippy
            placement="bottom-start"
            offset={[-3, -51]}
            interactive={true}
            trigger="click"
            zIndex={20}
            appendTo={
                document.querySelector('.fc-scroller.fc-scroller-liquid-absolute') || document.body
            }
            render={() => {
                return (
                    <div className="flex flex-col space-y-2 rounded-md border border-white bg-white p-2 shadow-icoSchedulerStackedPost dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite">
                        {isGroup ? (
                            <PostEventStackedFilter
                                profileFilter={profileFilter}
                                setProfileFilter={setProfileFilter}
                                stackedPosts={stackedPosts}
                                spGroups={spGroups}
                            />
                        ) : null}
                        <div className="scrollbar max-h-48 space-y-1">
                            {stackedPostsToDisplay.map((stackedPost) => {
                                const dateNow = dayjs().utc().tz(timezone).format();
                                const isFuture = dayjs(post?.scheduled_at)
                                    .utc()
                                    .tz(timezone)
                                    .isAfter(dateNow);
                                return (
                                    <div
                                        key={stackedPost.id}
                                        className={clsx({
                                            'cursor-pointer': typeof onClick === 'function'
                                        })}
                                        onClick={() => {
                                            if (typeof onClick === 'function') {
                                                onClick(stackedPost);
                                            }
                                        }}>
                                        <PostEventInStackWrapper
                                            classNameWrapper={clsx({
                                                'pointer-events-none': typeof onClick === 'function'
                                            })}
                                            post={stackedPost}
                                            isReadOnly={post?.read_only ?? false}
                                            timeFormat={timeFormat}
                                            timezone={timezone}
                                            isStacked={isFuture}
                                            style={{ width: eventRef?.current?.clientWidth }}
                                            settings={
                                                settings
                                                    ? settings({ dataPost: stackedPost })
                                                    : settings
                                            }
                                            clickAction={clickAction}
                                            isGroup={isGroup}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            }}>
            <PostEvent
                ref={eventRef}
                post={post}
                timeFormat={timeFormat}
                timezone={timezone}
                stackedPosts={stackedPosts}
                settings={settings ? settings({ dataPost: post, disablePopover: true }) : null}
                isGroup={isGroup}
            />
        </Tippy>
    );
};

PostEventStacked.propTypes = {
    post: PropTypes.object.isRequired,
    timeFormat: PropTypes.string.isRequired,
    timezone: PropTypes.string.isRequired,
    stackedPosts: PropTypes.array.isRequired,
    onClick: PropTypes.func,
    settings: PropTypes.func,
    clickAction: PropTypes.object,
    isGroup: PropTypes.bool,
    spGroups: PropTypes.array
};

export default PostEventStacked;
